import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { UnitMemberData } from "../types";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

export const useUnitMembersData = (currentUser: any, isUnitHead: boolean) => {
  const [unitMembersData, setUnitMembersData] = useState<UnitMemberData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { activeYear } = usePerformanceYear();

  // Function to calculate allocated band based on boundaries and rating
  const calculateAllocatedBand = (rating: number, boundaries: any): string => {
    if (rating === 0) return '-';
    if (rating < boundaries.line1) return 'D';
    if (rating < boundaries.line2) return 'C';
    if (rating < boundaries.line3) return 'B';
    return 'A';
  };

  // Helper function to check if a status should lock the band value
  const isStatusLocked = (status: string | null | undefined): boolean => {
    return status === 'Final Band Accepted' || 
           status === 'Band disagreement submitted' || 
           status === 'Band disagreement resolution completed';
  };

  const fetchTransitiveUnitMembers = async (unitHeadId: string): Promise<string[]> => {
    const allSubordinates: Set<string> = new Set();
    const visited: Set<string> = new Set();
    const queue: string[] = [unitHeadId];

    console.log('Starting transitive fetch for unit head:', unitHeadId);

    while (queue.length > 0) {
      const currentSupervisorId = queue.shift()!;
      
      if (visited.has(currentSupervisorId)) {
        continue;
      }
      visited.add(currentSupervisorId);

      console.log('Fetching direct reports for supervisor:', currentSupervisorId);

      // Get direct reports for current supervisor
      const { data: directReports, error } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('supervisor_id', currentSupervisorId);

      if (error) {
        console.error('Error fetching direct reports for', currentSupervisorId, ':', error);
        continue;
      }

      if (directReports && directReports.length > 0) {
        console.log(`Found ${directReports.length} direct reports for ${currentSupervisorId}:`, directReports);
        
        for (const report of directReports) {
          // Add to subordinates list (exclude the unit head themselves)
          if (report.id !== unitHeadId) {
            allSubordinates.add(report.id);
          }
          
          // Add to queue to check for their subordinates
          queue.push(report.id);
        }
      } else {
        console.log('No direct reports found for supervisor:', currentSupervisorId);
      }
    }

    const result = Array.from(allSubordinates);
    console.log('Total transitive unit members found:', result.length, result);
    return result;
  };

  const fetchUnitMembersData = async () => {
    if (!currentUser || !isUnitHead) {
      console.log('Not fetching unit data - currentUser:', !!currentUser, 'isUnitHead:', isUnitHead);
      setUnitMembersData([]);
      return;
    }

    setIsLoading(true);
    try {
      console.log('Fetching unit members data for unit head:', currentUser.id);
      
      const transitiveMembers = await fetchTransitiveUnitMembers(currentUser.id);
      console.log('Transitive unit members:', transitiveMembers);

      if (transitiveMembers.length === 0) {
        console.log('No transitive members found');
        setUnitMembersData([]);
        setIsLoading(false);
        return;
      }

      // Fetch user details
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, full_name, role')
        .in('id', transitiveMembers);

      if (usersError) {
        console.error('Error fetching users data:', usersError);
        setIsLoading(false);
        return;
      }

      console.log('Users data fetched:', usersData);

      // Fetch all performance appraisals for these users to calculate averages - FILTER BY ACTIVE YEAR
      const { data: allAppraisals, error: appraisalsError } = await supabase
        .from('performance_appraisals')
        .select('user_id, self_rating, manager_rating')
        .in('user_id', transitiveMembers)
        .eq('year', activeYear);

      if (appraisalsError) {
        console.error('Error fetching performance appraisals:', appraisalsError);
      }

      console.log('Performance appraisals fetched for year', activeYear, ':', allAppraisals);

      // Fetch existing performance bands for these users - FILTER BY ACTIVE YEAR
      const { data: allPerformanceBands, error: bandsError } = await supabase
        .from('performance_bands')
        .select('employee_id, self_rating, manager_rating, allocated_performance_band, final_banding, supervisor_banding_comments, status')
        .in('employee_id', transitiveMembers)
        .eq('year', activeYear);

      if (bandsError) {
        console.error('Error fetching performance bands:', bandsError);
      }

      console.log('Performance bands fetched for year', activeYear, '- PURE READ ONLY:', allPerformanceBands);

      const processedData: UnitMemberData[] = [];

      for (const user of usersData || []) {
        console.log(`Processing user: ${user.full_name} (ID: ${user.id})`);
        
        // Calculate average ratings from performance appraisals
        const userAppraisals = allAppraisals?.filter(appraisal => appraisal.user_id === user.id) || [];
        
        let calculatedSelfRating = 0;
        let calculatedManagerRating = 0;
        
        if (userAppraisals.length > 0) {
          const selfRatings = userAppraisals
            .map(a => a.self_rating)
            .filter(rating => rating !== null && rating !== undefined);
          
          const managerRatings = userAppraisals
            .map(a => a.manager_rating)
            .filter(rating => rating !== null && rating !== undefined);
          
          if (selfRatings.length > 0) {
            calculatedSelfRating = Number((selfRatings.reduce((sum, rating) => sum + rating, 0) / selfRatings.length).toFixed(2));
          }
          
          if (managerRatings.length > 0) {
            calculatedManagerRating = Number((managerRatings.reduce((sum, rating) => sum + rating, 0) / managerRatings.length).toFixed(2));
          }
        }

        console.log(`Calculated ratings for ${user.full_name}: Self=${calculatedSelfRating}, Manager=${calculatedManagerRating}`);

        // Find existing performance band data - PURE READ ONLY
        const performanceBandData = allPerformanceBands?.find(band => band.employee_id === user.id);
        
        // Use existing database values if available, otherwise use calculated ratings
        const finalSelfRating = performanceBandData?.self_rating || calculatedSelfRating || 0;
        const finalManagerRating = performanceBandData?.manager_rating || calculatedManagerRating || 0;
        
        // Calculate system banding based on manager rating (or self rating if no manager rating)
        const ratingForBanding = finalManagerRating || finalSelfRating || 0;
        let systemBanding = performanceBandData?.allocated_performance_band || '-';
        if (ratingForBanding > 0 && !performanceBandData?.allocated_performance_band) {
          systemBanding = calculateAllocatedBand(ratingForBanding, { line1: 2.5, line2: 3.0, line3: 3.5 });
        }

        // CRITICAL: READ EXISTING STATUS VALUE ONLY - NO MODIFICATIONS WHATSOEVER
        const readOnlyStatus = performanceBandData?.status || null;
        
        console.log(`STATUS READ ONLY for ${user.full_name}: "${readOnlyStatus}" - ZERO MODIFICATIONS`);

        const unitMemberData: UnitMemberData = {
          id: user.id,
          full_name: user.full_name || 'Unknown',
          role: user.role || '',
          self_rating: finalSelfRating,
          manager_rating: finalManagerRating,
          final_banding: performanceBandData?.final_banding || null,
          supervisor_banding_comments: performanceBandData?.supervisor_banding_comments || '',
          supervisor_name: currentUser?.full_name || '',
          // Aligned properties with TeamMemberData
          averageSelfRating: finalSelfRating,
          averageManagerRating: finalManagerRating,
          finalBandingScore: Number((finalManagerRating || finalSelfRating || 0).toFixed(2)),
          allocatedPerformanceBand: systemBanding,
          finalBanding: performanceBandData?.final_banding || null,
          supervisorBandingComments: performanceBandData?.supervisor_banding_comments || '',
          isCommentEditable: false,
          hasSavedRecord: !!performanceBandData,
          status: readOnlyStatus // PURE READ ONLY - NEVER MODIFIED
        };

        processedData.push(unitMemberData);
        console.log(`Unit member processed with READ-ONLY status "${readOnlyStatus}":`, unitMemberData);
      }

      console.log('Final unit members data with READ-ONLY statuses:', processedData);
      setUnitMembersData(processedData);
    } catch (error) {
      console.error('Error fetching unit members data:', error);
      setUnitMembersData([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced function to refresh statuses with cache-busting
  const refreshStatuses = async () => {
    if (!currentUser || !isUnitHead || unitMembersData.length === 0) {
      console.log('Cannot refresh statuses - conditions not met');
      return;
    }

    try {
      console.log('Refreshing status and final_banding values for unit members with cache-busting...');
      
      const employeeIds = unitMembersData.map(member => member.id);
      
      // Add cache-busting with timestamp and explicit ordering for fresh data
      const timestamp = new Date().getTime();
      
      // Fetch latest status AND final_banding values from performance_bands table with cache-busting
      const { data: latestData, error } = await supabase
        .from('performance_bands')
        .select('employee_id, status, final_banding')
        .in('employee_id', employeeIds)
        .eq('year', activeYear)
        .order('updated_at', { ascending: false }) // Ensure we get the most recent updates
        .order('employee_id', { ascending: true }); // Secondary ordering for consistency

      if (error) {
        console.error('Error fetching latest data with cache-busting:', error);
        throw error;
      }

      console.log(`Fresh status and final_banding data fetched at ${timestamp}:`, latestData);
      
      // Update the unit members data with latest status and final_banding values
      setUnitMembersData(prevData => {
        const updatedData = prevData.map(member => {
          const latestRecord = latestData?.find(s => s.employee_id === member.id);
          if (latestRecord) {
            console.log(`Updating data for ${member.full_name}:`);
            console.log(`  Status: "${member.status}" -> "${latestRecord.status}"`);
            console.log(`  Final Banding: "${member.finalBanding}" -> "${latestRecord.final_banding}"`);
            return {
              ...member,
              status: latestRecord.status,
              finalBanding: latestRecord.final_banding,
              final_banding: latestRecord.final_banding
            };
          }
          return member;
        });
        
        console.log('Unit members data updated with fresh status and final_banding values at', timestamp);
        return updatedData;
      });
      
    } catch (error) {
      console.error('Error refreshing status and final_banding values with cache-busting:', error);
      throw error; // Re-throw to let parent handle the error
    }
  };

  // Function to update unit members data based on new boundaries - only affects finalBanding (adjusted banding)
  // BUT preserve locked bands (Accepted, Disagreement, Resolved status)
  const updateUnitMembersWithBoundaries = (boundaries: any) => {
    const safeBoundaries = {
      ...boundaries,
      line4: boundaries.line4 || 5.0
    };
    
    setUnitMembersData(prevData => 
      prevData.map(member => {
        const score = member.manager_rating || member.averageManagerRating;
        let newAdjustedBanding = member.finalBanding; // Keep current adjusted banding as default
        
        // Only recalculate adjusted banding if we have a valid score AND the status is not locked
        if (score > 0 && !isStatusLocked(member.status)) {
          newAdjustedBanding = calculateAllocatedBand(score, safeBoundaries);
        }
        
        return {
          ...member,
          // allocatedPerformanceBand stays the same (system banding)
          finalBanding: newAdjustedBanding // This is the adjusted banding that changes only if not locked
        };
      })
    );
  };

  useEffect(() => {
    console.log('useUnitMembersData effect triggered - isUnitHead:', isUnitHead, 'currentUser:', !!currentUser, 'activeYear:', activeYear);
    if (isUnitHead && currentUser) {
      fetchUnitMembersData();
    } else {
      setUnitMembersData([]);
    }
  }, [currentUser, isUnitHead, activeYear]);

  return {
    unitMembersData,
    isLoading,
    setUnitMembersData,
    refetchUnitMembersData: fetchUnitMembersData,
    updateUnitMembersWithBoundaries,
    refreshStatuses
  };
};
